import { composeState, sessionState } from '../../state';
import { setCurrentSessionRunningState } from '../../session/manager';
import { RENDER_DEBOUNCE } from '../../constants';
import { createAssistantMetaBar, updateAssistantMetaLeft } from '../message-meta-bar';
import { focusComposerElement } from '../composer-focus';
import { logCodexBubbleDebug, logSessionDebug } from '../session-debug';
import { buildMessageSegmentElement, normalizeSegmentsForDisplay } from './assistant-segments';
import { copyText } from './clipboard';
import { getAbortBtn, getMessagesDiv, getMsgInput, getSendBtn } from './dom';
import { renderMarkdown } from './markdown';
import { createMsgElement, createTextSegmentElement } from './message-element';
import { scrollToBottom } from './scrollbar';
import type { AssistantSegment, ChatMessage } from './types';

let renderTimer: ReturnType<typeof setTimeout> | null = null;
let streamingBubbleSequence = 0;

function summarizeDomMessages(messagesDiv: HTMLElement | null): unknown[] {
  if (!messagesDiv) return [];
  return Array.from(messagesDiv.querySelectorAll('.msg')).slice(-12).map((message, index) => ({
    index,
    role: message.classList.contains('assistant') ? 'assistant' : message.classList.contains('user') ? 'user' : 'system',
    identity: message.getAttribute('data-message-identity') || null,
    bubbleId: message.getAttribute('data-bubble-id') || null,
    sessionId: message.getAttribute('data-session-id') || null,
    isStreaming: message.id === 'streaming-msg',
  }));
}

export function createStreamingPlaceholder(): HTMLDivElement {
  const placeholder = document.createElement('div');
  placeholder.className = 'msg-segment msg-segment-pending';
  placeholder.innerHTML = '<div class="typing-indicator"><span></span><span></span><span></span></div>';
  return placeholder;
}

export function getStreamingBubble(): Element | null {
  return document.querySelector('#streaming-msg .msg-bubble');
}

export function clearStreamingPlaceholder(bubble: Element | null): void {
  const placeholder = bubble?.querySelector('.msg-segment-pending');
  if (placeholder) placeholder.remove();
}

export function getStreamingContentTail(bubble: HTMLElement | null): Element | null {
  if (!bubble) return null;
  let node = bubble.lastElementChild;
  while (node && node.classList.contains('msg-meta-bar')) {
    node = node.previousElementSibling;
  }
  return node;
}

function ensureStreamingTextSegment(): HTMLElement | null {
  const bubble = getStreamingBubble();
  if (!(bubble instanceof HTMLElement)) return null;
  clearStreamingPlaceholder(bubble);
  const last = getStreamingContentTail(bubble);
  if (last && last.classList.contains('msg-segment-text')) return last as HTMLElement;
  const textDiv = createTextSegmentElement('', { phase: 'final' });
  const meta = bubble.querySelector('.msg-meta-bar');
  if (meta) bubble.insertBefore(textDiv, meta);
  else bubble.appendChild(textDiv);
  return textDiv;
}

function ensureBubbleHasPlaceholder(bubble: Element | null): void {
  if (!(bubble instanceof HTMLElement) || getStreamingContentTail(bubble)) return;
  bubble.innerHTML = '';
  bubble.appendChild(createStreamingPlaceholder());
}

function appendNewStreamingMessage(messagesDiv: HTMLElement): void {
  const msgEl = createMsgElement('assistant', '');
  msgEl.id = 'streaming-msg';
  msgEl.dataset.bubbleId = `bubble-${++streamingBubbleSequence}`;
  if (sessionState.currentSessionId) msgEl.dataset.sessionId = sessionState.currentSessionId;
  ensureBubbleHasPlaceholder(msgEl.querySelector('.msg-bubble'));
  messagesDiv.appendChild(msgEl);
}

function adoptLastAssistantAsStreaming(messagesDiv: HTMLElement): boolean {
  if (!(messagesDiv.lastElementChild instanceof HTMLElement) || !messagesDiv.lastElementChild.classList.contains('assistant')) {
    return false;
  }
  messagesDiv.lastElementChild.id = 'streaming-msg';
  if (!messagesDiv.lastElementChild.dataset.bubbleId) {
    messagesDiv.lastElementChild.dataset.bubbleId = `bubble-${++streamingBubbleSequence}`;
  }
  if (sessionState.currentSessionId) messagesDiv.lastElementChild.dataset.sessionId = sessionState.currentSessionId;
  ensureBubbleHasPlaceholder(messagesDiv.lastElementChild.querySelector('.msg-bubble'));
  return true;
}

export function startGenerating(source = 'unknown'): void {
  const messagesDiv = getMessagesDiv();
  const sendBtn = getSendBtn();
  const abortBtn = getAbortBtn();
  composeState.isGenerating = true;
  setCurrentSessionRunningState(true);
  composeState.pendingText = '';
  composeState.activeToolCalls.clear();
  if (sendBtn) sendBtn.hidden = sessionState.currentAgent !== 'codex';
  if (abortBtn) abortBtn.hidden = false;
  logSessionDebug('stop-debug:start-generating', {
    sessionId: sessionState.currentSessionId || null,
    currentSessionRunning: !!sessionState.currentSessionRunning,
    sendHidden: !!sendBtn?.hidden,
    abortHidden: !!abortBtn?.hidden,
    pendingTextLength: composeState.pendingText.length,
  });

  const welcome = messagesDiv?.querySelector('.welcome-msg');
  if (welcome) welcome.remove();

  const existing = document.getElementById('streaming-msg');
  if (existing && sessionState.currentSessionId && existing.dataset.sessionId
    && existing.dataset.sessionId !== sessionState.currentSessionId) {
    logCodexBubbleDebug('remove-stale-streaming-message', {
      source,
      sessionId: sessionState.currentSessionId,
      staleSessionId: existing.dataset.sessionId,
    });
    existing.remove();
  }
  const activeStreaming = document.getElementById('streaming-msg');
  if (sessionState.currentAgent === 'codex') {
    logCodexBubbleDebug('start-generating', {
      source,
      sessionId: sessionState.currentSessionId || null,
      assistantCount: messagesDiv?.querySelectorAll('.msg.assistant').length || 0,
      hasStreamingMessage: !!activeStreaming,
      lastChildClass: messagesDiv?.lastElementChild?.className || null,
      activeBubbleId: activeStreaming?.getAttribute('data-bubble-id') || null,
      domMessages: summarizeDomMessages(messagesDiv),
    });
  }
  if (!activeStreaming && messagesDiv?.lastElementChild instanceof HTMLElement && messagesDiv.lastElementChild.classList.contains('assistant')) {
    adoptLastAssistantAsStreaming(messagesDiv);
  } else if (!activeStreaming) {
    if (messagesDiv) appendNewStreamingMessage(messagesDiv);
  }
  scrollToBottom(true);
}

// 独立 Codex turn 开始时封存当前回复，并创建新的流式回复气泡。
export function startNextStreamingTurn(): void {
  const messagesDiv = getMessagesDiv();
  if (!messagesDiv) return;
  logCodexBubbleDebug('before-bubble-switch', {
    assistantCount: messagesDiv.querySelectorAll('.msg.assistant').length,
    hasStreamingMessage: !!document.getElementById('streaming-msg'),
    pendingTextLength: composeState.pendingText.length,
    activeToolCallCount: composeState.activeToolCalls.size,
    activeBubbleId: document.getElementById('streaming-msg')?.getAttribute('data-bubble-id') || null,
    domMessages: summarizeDomMessages(messagesDiv),
  });
  flushRender();
  const current = document.getElementById('streaming-msg');
  if (current) current.removeAttribute('id');
  composeState.pendingText = '';
  composeState.activeToolCalls.clear();
  appendNewStreamingMessage(messagesDiv);
  logCodexBubbleDebug('after-bubble-switch', {
    assistantCount: messagesDiv.querySelectorAll('.msg.assistant').length,
    hasStreamingMessage: !!document.getElementById('streaming-msg'),
    activeBubbleId: document.getElementById('streaming-msg')?.getAttribute('data-bubble-id') || null,
    domMessages: summarizeDomMessages(messagesDiv),
  });
  scrollToBottom(true);
}

export function ensureStreamingPlaceholderVisible(): void {
  const messagesDiv = getMessagesDiv();
  const sendBtn = getSendBtn();
  const abortBtn = getAbortBtn();
  if (!messagesDiv) return;
  composeState.isGenerating = true;
  if (sendBtn) sendBtn.hidden = sessionState.currentAgent !== 'codex';
  if (abortBtn) abortBtn.hidden = false;
  logSessionDebug('stop-debug:ensure-placeholder', {
    sessionId: sessionState.currentSessionId || null,
    hasStreamingMessage: !!document.getElementById('streaming-msg'),
    sendHidden: !!sendBtn?.hidden,
    abortHidden: !!abortBtn?.hidden,
  });
  const existing = document.getElementById('streaming-msg');
  if (existing) {
    ensureBubbleHasPlaceholder(existing.querySelector('.msg-bubble'));
    return;
  }
  if (adoptLastAssistantAsStreaming(messagesDiv)) {
    return;
  }
  appendNewStreamingMessage(messagesDiv);
}

export function finishGenerating(sessionId?: string, source = 'unknown'): void {
  const sendBtn = getSendBtn();
  const abortBtn = getAbortBtn();
  const msgInput = getMsgInput();
  composeState.isGenerating = false;
  setCurrentSessionRunningState(false);
  if (sendBtn) sendBtn.hidden = false;
  if (abortBtn) abortBtn.hidden = true;
  logSessionDebug('stop-debug:finish-generating', {
    sessionId: sessionId || sessionState.currentSessionId || null,
    source,
    pendingTextLength: composeState.pendingText.length,
    activeToolCallCount: composeState.activeToolCalls.size,
    sendHidden: !!sendBtn?.hidden,
    abortHidden: !!abortBtn?.hidden,
    activeBubbleId: document.getElementById('streaming-msg')?.getAttribute('data-bubble-id') || null,
    domMessages: summarizeDomMessages(getMessagesDiv()),
  });
  focusComposerElement(msgInput);

  if (composeState.pendingText) flushRender();

  const streamEl = document.getElementById('streaming-msg');
  if (streamEl) streamEl.removeAttribute('id');

  if (sessionId && (!sessionState.currentSessionId || sessionState.currentSessionId === sessionId)) {
    sessionState.currentSessionId = sessionId;
  }
  composeState.pendingText = '';
  composeState.activeToolCalls.clear();
}

export function scheduleRender(): void {
  if (renderTimer) return;
  renderTimer = setTimeout(() => {
    renderTimer = null;
    flushRender();
  }, RENDER_DEBOUNCE);
}

export function flushRender(): void {
  if (!composeState.pendingText) return;
  const textDiv = ensureStreamingTextSegment();
  if (!textDiv) return;
  const nextText = `${textDiv.dataset.rawText || ''}${composeState.pendingText}`;
  textDiv.dataset.rawText = nextText;
  textDiv.innerHTML = renderMarkdown(nextText);
  composeState.pendingText = '';
  scrollToBottom();
}

export function renderStreamingSegments(segments: AssistantSegment[]): void {
  const bubble = getStreamingBubble();
  if (!bubble) return;
  const metaEl = bubble.querySelector('.msg-meta-bar');
  bubble.innerHTML = '';
  normalizeSegmentsForDisplay(segments).forEach((segment) => {
    const segmentEl = buildMessageSegmentElement(segment);
    if (segmentEl) bubble.appendChild(segmentEl);
  });
  if (metaEl) bubble.appendChild(metaEl);
}

export function updateStreamingAssistantMeta(message: ChatMessage): void {
  const bubble = getStreamingBubble();
  if (!(bubble instanceof HTMLElement)) return;
  if (!bubble.querySelector('.msg-meta-bar')) {
    bubble.appendChild(createAssistantMetaBar({ message, copyText }));
    return;
  }
  updateAssistantMetaLeft(bubble, message);
}

export function markStreamingProcessTextSegments(): void {
  const bubble = getStreamingBubble();
  if (!bubble) return;
  bubble.querySelectorAll('.msg-segment-text').forEach((segment) => {
    segment.classList.remove('msg-segment-final');
    segment.classList.add('msg-segment-process');
    (segment as HTMLElement).dataset.phase = 'process';
  });
}
